/**
 * The arithmetic behind PostersTab, kept apart from the rendering.
 *
 * Split out for one reason: this is the part that can actually be wrong.
 * Timezone-keyed day buckets, "poster with zero scans still needs a row", and
 * the share-of-total maths are all easy to get subtly incorrect and impossible
 * to eyeball in a screenshot. Living in a file with no React and no MUI
 * imports, it can be exercised with plain `node`.
 *
 * Nothing here touches the network or the DOM.
 */

/** What each QR tag is, in plain words. Unknown tags still render, just raw. */
export const POSTERS = {
    p1: { name: 'Poster 1 — Editorial', blurb: '$10 all day, with the full service list' },
    p2: { name: 'Poster 2 — Big sign', blurb: '$10 all day, one big number' },
    p3: { name: 'Poster 3 — The move', blurb: '$15 street-cleaning move, how it works' },
};

/**
 * The last `n` dates as YYYY-MM-DD in New York, oldest last — the same keys
 * /api/scan writes, so they line up without any timezone guessing.
 * `now` is injectable so this can be tested at a fixed instant.
 */
export function recentDays(n, now = Date.now()) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const out = [];
    for (let i = n - 1; i >= 0; i -= 1) {
        out.push(fmt.format(new Date(now - i * 86400000)));
    }
    return out;
}

export const sumDays = (byDay, days) =>
    days.reduce((total, day) => total + Number((byDay || {})[day] || 0), 0);

/** "3 days ago" beats a timestamp for someone glancing at this once a week. */
export function whenAgo(iso, now = Date.now()) {
    if (!iso) return 'never';
    const ms = now - new Date(iso).getTime();
    if (Number.isNaN(ms)) return 'never';
    if (ms < 60000) return 'just now';
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** "2026-08-10" → "08/10". */
export const shortDay = (key) => key.slice(5).replace('-', '/');

/** "Poster 3" — the KPI tile wants the name without the descriptive half. */
export const shortName = (src) => (POSTERS[src] ? POSTERS[src].name.split(' — ')[0] : src);

/**
 * Everything the tab draws, derived from the raw /api/scan payload.
 *
 * Every poster we printed gets a row even at zero. A poster nobody has scanned
 * is the single most useful thing this page can say, and it cannot say it by
 * leaving the row out — so the known tags are merged in before sorting.
 */
export function summarise(payload, { days = 14, now = Date.now() } = {}) {
    const dayKeys = recentDays(days, now);
    const rows = (payload && payload.scans ? payload.scans : []).map((s) => ({
        src: s.src,
        count: Number(s.count || 0),
        lastAt: s.lastAt || null,
        byDay: s.byDay || {},
    }));

    const seen = new Set(rows.map((r) => r.src));
    Object.keys(POSTERS).forEach((src) => {
        if (!seen.has(src)) rows.push({ src, count: 0, lastAt: null, byDay: {} });
    });
    rows.sort((a, b) => b.count - a.count || a.src.localeCompare(b.src));

    const total = rows.reduce((n, r) => n + r.count, 0);
    const weekKeys = dayKeys.slice(-7);
    const perDay = dayKeys.map((day) => ({
        day,
        n: rows.reduce((n, r) => n + Number(r.byDay[day] || 0), 0),
    }));

    return {
        rows,
        total,
        lastWeek: rows.reduce((n, r) => n + sumDays(r.byDay, weekKeys), 0),
        best: rows[0] || null,
        // Guard the divisors: a fresh install has every count at zero, and the
        // bars must render at 0% rather than NaN%.
        most: Math.max(1, ...rows.map((r) => r.count)),
        perDay,
        peak: Math.max(1, ...perDay.map((d) => d.n)),
    };
}
